import { LoginStatusContext } from "app/context/LoginStatusContext";

import { WebPortalDashboardBookingData, WebPortalDashboardInitialData, WebPortalDashboardQuoteData } from "app/types/dataInterfaces";
import { ShipmentDirection } from "app/types/enums";
import axios, { AxiosResponse } from "axios";
import { useContext, useEffect, useRef, useState } from "react";


const useDashboardData = () => {
    const [initialData, setInitialData] = useState<WebPortalDashboardInitialData[] | undefined>(undefined);
    const [searchData, setSearchData] = useState<WebPortalDashboardInitialData[] | undefined>(undefined);
    const [searchDataLoading, setSearchDataLoading] = useState(false);
    const [initialDataLoading, setInitialDataLoading] = useState(false);
    const [bookingData, setBookingData] = useState<WebPortalDashboardBookingData[]>([]);
    const [filteredBookingData, setFilteredBookingData] = useState<WebPortalDashboardBookingData[] | undefined>(undefined);
    const [bookingDataLoading, setBookingDataLoading] = useState(false);
    const [quoteData, setQuoteData] = useState<WebPortalDashboardQuoteData[] | undefined>(undefined);
    const [quoteDataLoading, setQuoteDataLoading] = useState(false);
    const [bookingFilter, setBookingFilter] = useState<ShipmentDirection>(ShipmentDirection.Import);
    const [error, setError] = useState<string | undefined>(undefined);
    const [requiresUpdate, setRequiresUpdate] = useState(false);

    useEffect(() => {            
        const storedDateStr = sessionStorage.getItem('lastUpdated');
    
        if(storedDateStr) {
            const storedDate = new Date(storedDateStr);
            const now = new Date();
            const differenceInMilliseconds = now.getTime() - storedDate.getTime();
            const thirtyMinutesInMilliseconds = 30 * 60 * 1000;
        
            // last updated exists, see whether it's in the last 30 mins
            if(differenceInMilliseconds > thirtyMinutesInMilliseconds) {
                setRequiresUpdate(true);
            }
            // last updated does not exist
        } else {
            setRequiresUpdate(true);
        }
    }, [])

    useEffect(() => {
        const fetchInitialData = () => {
            setError(undefined);
            setInitialDataLoading(true);
            const url = `${process.env.REACT_APP_API_ENDPOINT}/api/shipment/GetDashboardInitialData`;
            axios.get(url).then((res) => {
                setInitialData(res.data);
                setInitialDataLoading(false);
            })
            .catch((error) => {
                const errorMessage = `An error occured whilst fetching table data with the error: ${error}`;
                setInitialDataLoading(false);
                setError(errorMessage);
                console.error(errorMessage);
            });
        }
        if (!initialData) {
            fetchInitialData();
        }
    }, [initialData]);

    useEffect(() => {
        if (initialData && initialData.length > 0) {
            const now = new Date();
            sessionStorage.setItem('lastUpdated', now.toISOString());
        }
    }, [initialData])

    useEffect(() => {
        const updateInitialData = () => {
                const url = `${process.env.REACT_APP_API_ENDPOINT}/api/shipment/UpdateInitialData`;
                axios.get(url)
                    .catch((error) => {
                        const errorMessage = `An error occured whilst fetching table data with the error: ${error}`;
                        console.error(errorMessage);
                    });
        };
        if (requiresUpdate) {
            updateInitialData();
        }
    }, [requiresUpdate])

    useEffect(() => {
        if (!searchData) {
            const fromDate = new Date(new Date().getFullYear(), 0, 1);
            const toDate = new Date(new Date().getFullYear(), 11, 31);
            toDate.setHours(23, 59, 59, 0);
            fetchSearchData(fromDate, toDate);
        }
    }, [searchData]);

    useEffect(() => {
        setFilteredBookingData(bookingData?.filter((booking) => booking.shipmentDirection === bookingFilter));
        setBookingDataLoading(false);
    }, [bookingData, bookingFilter])

    const fetchBookingData = () => {
        if (!bookingData || bookingData.length === 0) {
                setError(undefined);
                setBookingDataLoading(true);
                const url = `${process.env.REACT_APP_API_ENDPOINT}/api/shipment/GetWebPortalDashboardBookingData`;
                axios.get(url)
                    .then((res) => {
                        setBookingData(res.data);
                        setBookingDataLoading(false);
                    })
                    .catch((error) => {
                        const errorMessage = `An error occured whilst fetching booking data with the error: ${error}`;
                        setError(errorMessage);
                        console.error(errorMessage);
                    });
        }
    }

    const fetchQuoteData = () => {
        if (!quoteData || quoteData.length === 0) {
            setError(undefined);
            setQuoteDataLoading(true);
            const url = `${process.env.REACT_APP_API_ENDPOINT}/api/shipment/GetWebPortalDashboardQuoteData`;
            axios.get(url)
                .then((res) => {
                    setQuoteData(res.data);
                    setQuoteDataLoading(false);
                })
                .catch((error) => {
                    const errorMessage = `An error occured whilst fetching quote data with the error: ${error}`;
                    setError(errorMessage);
                    console.error(errorMessage);
                })
        }
    }

    const fetchSearchData = async (fromDate: Date, toDate: Date) => {
        try {
            setSearchDataLoading(true);
            const url = `${process.env.REACT_APP_API_ENDPOINT}/api/shipment/GetDashboardSearchData`;
            const res: AxiosResponse<WebPortalDashboardInitialData[]> = await axios.get(url, { params: { fromDate: fromDate, toDate: toDate } });
            setSearchData(res.data);
            setSearchDataLoading(false);
        } catch (error) {
            console.error(`An error occured whilst fetching search data with the error ${error}`);
            setSearchDataLoading(false);
        }
    }

    return {
        initialData,
        initialDataLoading,
        fetchBookingData,
        bookingFilter,
        setBookingFilter,
        filteredBookingData,
        bookingDataLoading,
        setBookingData,
        fetchQuoteData,
        quoteData,
        quoteDataLoading,
        setQuoteData,
        searchData,
        fetchSearchData,
        searchDataLoading,
        error
    }
}

export default useDashboardData;